import React, { useCallback } from 'react';
import { LogList } from '../../../../shared/LogList';
import { getIntl } from '../../../../../utils/intl';
import GuidanceContent from './GuidanceContent';
import { useIsFocused } from '@react-navigation/native';
import { useGuidanceAction } from '../../../../../hooks/useGuidanceAction';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import { useSelectedProfileIds } from './useSelectedProfileIds';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { useActivityTabNavigation } from '../../../../../utils/appNavigationNames';
import { useMyOrganizationClientInvitations } from '../../../../../hooks/api/useMyOrganizationClientInvitations';
import PendingOrganizationClientInvitationLogCard from './PendingOrganizationClientInvitationLogCard';
import { LogPressArgs } from '../../../../shared/LogList/LogList';

const intl = getIntl('ActivityScreen', {
    activity: 'Activity',
    selectProfileToView: 'Select A Profile To View Activity',
    filterByProfile: 'Filter By Profile',
    messageProfiles: 'Message Profiles',
    messageProfile: 'Message Profile',
});

export type ActivityScreenOwnProps = {};

export type ActivityScreenProps = ActivityScreenOwnProps;

const ActivityScreen: React.FC<ActivityScreenProps> = () => {
    const { isLoadingClientInvitations, clientInvitations } = useMyOrganizationClientInvitations({});
    const activityNavigator = useActivityTabNavigation();
    const { navigate } = useActivityTabNavigation();
    const { isLoadingProfiles, selectedProfileIds, onSetProfileIds, onToggleProfileId, profiles } =
        useSelectedProfileIds();
    const isFocused = useIsFocused();
    const guidanceAction = useGuidanceAction({
        title: intl('activity'),
        content: <GuidanceContent />,
    });

    const onLogPress = useCallback(
        ({ auditLogId, medicalProfileId }: LogPressArgs) => {
            if (medicalProfileId) {
                activityNavigator.navigate('details', {
                    auditLogId: auditLogId,
                    profileId: medicalProfileId!,
                });
            }
        },
        [activityNavigator],
    );

    const getPreLogListCards = useCallback(() => {
        if (isLoadingClientInvitations) {
            return [];
        }

        return clientInvitations.map((clientInvitation) => (
            <PendingOrganizationClientInvitationLogCard
                key={clientInvitation.organizationClientInvitationId}
                clientInvitation={clientInvitation}
            />
        ));
    }, [clientInvitations, isLoadingClientInvitations]);

    if (!isFocused) return null;

    return (
        <>
            <AnimateLoadedContent
                isLoading={isLoadingProfiles || isLoadingClientInvitations}
                style={{
                    flex: 1,
                }}
            >
                {() => (
                    <ActionButtonLayout
                        actions={[
                            {
                                title: selectedProfileIds.length > 1 ? intl('messageProfiles') : intl('messageProfile'),
                                isDisabled: !selectedProfileIds.length,
                                onPress: () => navigate('add-message', { profileIds: selectedProfileIds }),
                                icon: 'MessageSquare',
                                show: selectedProfileIds.length > 0,
                                testID: 'message-profiles',
                            },
                        ]}
                    >
                        <LogList
                            isLoadingProfiles={isLoadingProfiles}
                            selectedProfileIds={selectedProfileIds}
                            onSetProfileIds={onSetProfileIds}
                            onToggleProfileId={onToggleProfileId}
                            profiles={profiles}
                            onLogPress={onLogPress}
                            getPreLogListCards={getPreLogListCards}
                        />
                    </ActionButtonLayout>
                )}
            </AnimateLoadedContent>
            {guidanceAction.Prompt}
        </>
    );
};

export default React.memo(ActivityScreen);
